import { useLocation } from "react-router-dom";
import { home } from "./home";
import { rsvp } from "./rsvp";
import { rpe } from "./rpe";
import { feedback } from "./feedback";
import type { SupportedMarketingLocale } from "./types";

/**
 * Marketing pages live at four URL shapes:
 *   - English (default): /, /rsvp-info, /rpe-info, /feedback-info
 *   - Spanish (LATAM):   /es/...
 *   - Portuguese (BR):   /pt-br/...
 *   - French:            /fr/...
 *
 * Detection is path-based (not the i18next picker) because marketing pages
 * need stable, indexable URLs per locale for SEO + hreflang. The picker
 * navigates between localized URLs on these routes.
 */

/** Marketing-locale URL prefixes (no leading slash). Order matches detection priority. */
const PREFIXES: Record<Exclude<SupportedMarketingLocale, "en">, string> = {
  es: "es",
  "pt-BR": "pt-br",
  fr: "fr",
  ja: "ja",
};

export function useMarketingLocale(): SupportedMarketingLocale {
  const { pathname } = useLocation();
  return detectLocaleFromPath(pathname);
}

export function detectLocaleFromPath(pathname: string): SupportedMarketingLocale {
  const lower = pathname.toLowerCase();
  for (const [locale, prefix] of Object.entries(PREFIXES) as [
    Exclude<SupportedMarketingLocale, "en">,
    string,
  ][]) {
    if (lower === `/${prefix}` || lower.startsWith(`/${prefix}/`)) {
      return locale;
    }
  }
  return "en";
}

/** Strip any locale prefix to get the canonical English path for hreflang/canonical. */
export function stripLocalePrefix(pathname: string): string {
  const lower = pathname.toLowerCase();
  for (const prefix of Object.values(PREFIXES)) {
    if (lower === `/${prefix}`) return "/";
    if (lower.startsWith(`/${prefix}/`)) return pathname.slice(prefix.length + 1);
  }
  return pathname;
}

/** Build a localized URL for any English marketing path + target locale. */
export function localizedHref(
  englishPath: string,
  locale: SupportedMarketingLocale,
): string {
  const path = englishPath.startsWith("/") ? englishPath : `/${englishPath}`;
  if (locale === "en") return path === "" ? "/" : path;
  const prefix = PREFIXES[locale];
  if (path === "/" || path === "") return `/${prefix}`;
  return `/${prefix}${path}`;
}

/** @deprecated Use localizedHref(path, "es"). Kept as a shim to avoid churn. */
export function spanishHref(englishPath: string): string {
  return localizedHref(englishPath, "es");
}

export const marketing = { home, rsvp, rpe, feedback };
export type { SupportedMarketingLocale } from "./types";
